"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import { Phone } from "lucide-react"
import Card from "../components/Card"
import Input from "../components/Input"
import Button from "../components/Button"
import Modal from "../components/Modal"
import { purchaseAirtime } from "../utils/api"
import toast from "react-hot-toast"

const BuyAirtime = () => {
  const { user } = useAuth()
  const { addTransaction, deductBalance } = useWallet()
  const navigate = useNavigate()

  const [selectedNetwork, setSelectedNetwork] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [amount, setAmount] = useState("")
  const [purchasing, setPurchasing] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [errors, setErrors] = useState({})

  const networks = [
    { id: "mtn", name: "MTN", color: "bg-yellow-500", logo: "🟡" },
    { id: "airtel", name: "Airtel", color: "bg-red-500", logo: "🔴" },
    { id: "glo", name: "Glo", color: "bg-green-500", logo: "🟢" },
    { id: "9mobile", name: "9mobile", color: "bg-green-600", logo: "🟢" },
  ]

  const quickAmounts = [100, 200, 500, 1000, 2000, 5000]

  useEffect(() => {
    if (!user) {
      navigate("/login")
    }
  }, [user, navigate])

  const validateForm = () => {
    const newErrors = {}

    if (!selectedNetwork) {
      newErrors.network = "Please select a network"
    }

    if (!phoneNumber) {
      newErrors.phone = "Phone number is required"
    } else if (!/^(\+234|0)[789][01]\d{8}$/.test(phoneNumber)) {
      newErrors.phone = "Please enter a valid Nigerian phone number"
    }

    if (!amount) {
      newErrors.amount = "Amount is required"
    } else if (isNaN(amount) || Number.parseFloat(amount) < 50) {
      newErrors.amount = "Minimum amount is ₦50"
    } else if (Number.parseFloat(amount) > 50000) {
      newErrors.amount = "Maximum amount is ₦50,000"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePurchase = () => {
    if (!validateForm()) return

    const purchaseAmount = Number.parseFloat(amount)
    if (user.balance < purchaseAmount) {
      toast.error("Insufficient wallet balance")
      return
    }

    setShowConfirmModal(true)
  }

  const confirmPurchase = async () => {
    setPurchasing(true)
    setShowConfirmModal(false)

    try {
      const purchaseResponse = await purchaseAirtime({
        network: selectedNetwork,
        phone: phoneNumber,
        amount: Number.parseFloat(amount),
      })

      if (purchaseResponse.code === "000") {
        // Deduct from wallet
        deductBalance(Number.parseFloat(amount))

        // Add transaction
        addTransaction({
          type: "Airtime Purchase",
          network: selectedNetwork.toUpperCase(),
          phone: phoneNumber,
          amount: Number.parseFloat(amount),
          status: "completed",
          transactionId: purchaseResponse.content.transactions.transactionId,
        })

        toast.success("Airtime purchase successful!")

        // Reset form
        setSelectedNetwork("")
        setPhoneNumber("")
        setAmount("")

        // Redirect to transactions
        setTimeout(() => {
          navigate("/transactions")
        }, 2000)
      } else {
        toast.error("Purchase failed. Please try again.")
      }
    } catch (error) {
      toast.error("Purchase failed. Please try again.")
    } finally {
      setPurchasing(false)
    }
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Phone className="mr-3 text-green-600" />
            Buy Airtime
          </h1>
          <p className="text-gray-600 mt-2">Top up airtime for any Nigerian network instantly</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-xl font-semibold mb-6">Purchase Airtime</h2>

              {/* Network Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Choose Network</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {networks.map((network) => (
                    <div
                      key={network.id}
                      onClick={() => {
                        setSelectedNetwork(network.id)
                        setErrors({})
                      }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedNetwork === network.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="text-center">
                        <div
                          className={`w-12 h-12 ${network.color} rounded-full flex items-center justify-center mx-auto mb-2`}
                        >
                          <span className="text-xl">{network.logo}</span>
                        </div>
                        <p className="font-medium">{network.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.network && <p className="mt-1 text-sm text-red-600">{errors.network}</p>}
              </div>

              {/* Phone Number */}
              <Input
                label="Phone Number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value)
                  if (errors.phone) {
                    setErrors({ ...errors, phone: "" })
                  }
                }}
                error={errors.phone}
                placeholder="e.g., 08123456789"
              />

              {/* Amount */}
              <div className="mb-6">
                <Input
                  label="Amount (₦)"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value)
                    if (errors.amount) {
                      setErrors({ ...errors, amount: "" })
                    }
                  }}
                  error={errors.amount}
                  placeholder="Enter amount"
                  min="50"
                  max="50000"
                />

                {/* Quick Amount Buttons */}
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Quick amounts:</p>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {quickAmounts.map((quickAmount) => (
                      <Button
                        key={quickAmount}
                        variant="outline"
                        size="sm"
                        onClick={() => setAmount(quickAmount.toString())}
                        className={amount === quickAmount.toString() ? "bg-blue-50 border-blue-500" : ""}
                      >
                        ₦{quickAmount}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                onClick={handlePurchase}
                disabled={!selectedNetwork || !phoneNumber || !amount || purchasing}
                loading={purchasing}
                className="w-full"
              >
                Purchase Airtime
              </Button>
            </Card>
          </div>

          {/* Summary Section */}
          <div>
            <Card>
              <h3 className="text-lg font-semibold mb-4">Purchase Summary</h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Network:</span>
                  <span className="font-medium">{selectedNetwork ? selectedNetwork.toUpperCase() : "-"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{phoneNumber || "-"}</span>
                </div>

                <hr />

                <div className="flex justify-between text-lg font-bold">
                  <span>Amount:</span>
                  <span className="text-green-600">₦{amount ? Number.parseFloat(amount).toLocaleString() : "0"}</span>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Wallet Balance:</span>
                    <span
                      className={user.balance >= (Number.parseFloat(amount) || 0) ? "text-green-600" : "text-red-600"}
                    >
                      ₦{user.balance?.toLocaleString() || "0"}
                    </span>
                  </div>
                  {amount && user.balance < Number.parseFloat(amount) && (
                    <p className="text-red-600 text-xs mt-1">Insufficient balance. Please fund your wallet.</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Tips Card */}
            <Card className="mt-6">
              <h4 className="font-semibold mb-3">💡 Tips</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Minimum amount: ₦50</li>
                <li>• Maximum amount: ₦50,000</li>
                <li>• Airtime is delivered instantly</li>
                <li>• You can top up for any number</li>
              </ul>
            </Card>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Purchase">
          <div className="space-y-4">
            <p>Are you sure you want to purchase airtime?</p>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Network:</span>
                <span className="font-medium">{selectedNetwork?.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span>Phone:</span>
                <span className="font-medium">{phoneNumber}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Amount:</span>
                <span className="text-green-600">₦{Number.parseFloat(amount || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="flex space-x-4">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmPurchase} loading={purchasing} className="flex-1">
                Confirm Purchase
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default BuyAirtime
